<template>
  <div class="text-block flex flex-col gap-4 sm:flex-row">
    <!-- Mascot on the left (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'left'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="computedMascotIds"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>

    <!-- Main Content Section -->
    <div class="w-full overflow-hidden bg-white shadow-sm" :class="contentClasses">
      <div class="mx-auto w-full max-w-[650px] p-6 pb-0 md:max-w-[750px] md:p-10 md:pb-0">
        <!-- Title -->
        <h3 v-if="title" class="mb-4 text-left font-headline text-h3 font-normal text-black">
          {{ title }}
        </h3>

        <!-- Collapsible Content -->
        <div v-if="isCollapsible" class="relative">
          <!-- Content with height-based collapse -->
          <div
            class="text-left font-body text-body font-normal text-black transition-all duration-300 ease-in-out"
            :class="isExpanded ? 'max-h-none' : 'max-h-[75px]'"
          >
            <contentful-rich-text :document="data?.fields?.body" />
          </div>

          <!-- Gradient fade overlay when collapsed -->
          <div
            v-if="!isExpanded"
            class="pointer-events-none absolute bottom-0 bottom-[50px] left-0 right-0 h-16 md:bottom-[85px]"
            style="
              background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.03) 2.5%,
                rgba(255, 255, 255, 0.8) 28.5%,
                #fff 100%
              );
            "
          ></div>

          <!-- Toggle Button (centered at bottom) -->
          <div class="relative mt-4 flex w-full justify-center bg-white pb-6 md:pb-8">
            <app-button
              type="toggle"
              variant="lila"
              :expanded="isExpanded"
              size="desktop"
              arrow-type="double"
              @click="toggleExpanded"
            >
              {{ isExpanded ? 'Läs mindre' : 'Läs mer' }}
            </app-button>
          </div>
        </div>

        <!-- Non-collapsible Content -->
        <div
          v-else
          class="text-block-content pb-6 text-left font-body text-body font-normal text-black md:pb-8"
        >
          <contentful-rich-text :document="data?.fields?.body" />
        </div>
      </div>
    </div>

    <!-- Mascot on the right (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'right'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="computedMascotIds"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMascots } from '@/composables/useMascots'

interface Props {
  textBlockData: any
}

const props = defineProps<Props>()

// Use the provided data directly
const data = computed(() => props.textBlockData)

// Extract data from TextBlock
const title = computed(() => data.value?.fields?.rubrik || '')
const mascot = computed(() => data.value?.fields?.mascot || [])
const mascotPlacement = computed(() => data.value?.fields?.mascotPlacement || [])

// Mascot configuration (same as CtaBanner)
const { getMascotConfig } = useMascots()
const mascotConfig = computed(() => getMascotConfig('textblock'))

// Mascot logic (same as CtaBanner)
const shouldShowMascots = computed(() => {
  return mascot.value && mascot.value.length > 0
})

const computedMascotIds = computed(() => {
  return mascot.value || []
})

const computedMascotPosition = computed((): 'left' | 'right' => {
  if (mascotPlacement.value && mascotPlacement.value.length > 0) {
    // Map Swedish Contentful values to our component values
    return mascotPlacement.value[0] === 'Vänster' ? 'left' : 'right'
  }
  return 'left' // Default to left if no placement specified
})

const mascotVariant = computed(() => {
  return 'neutral' as const
})

// Mascot container classes with justify-content (same as CtaBanner)
const mascotContainerClasses = computed((): string => {
  const baseClasses = 'pointer-events-none flex flex-shrink-0 '
  const justifyClass =
    computedMascotPosition.value === 'left' ? 'justify-start items-start' : 'justify-end self-end'
  return `${baseClasses} ${justifyClass}`
})

// Content classes with border radius logic (same as CtaBanner)
const contentClasses = computed((): string => {
  const baseClasses = 'rounded-[20px]'

  if (shouldShowMascots.value) {
    if (computedMascotPosition.value === 'left') {
      return `${baseClasses} rounded-tl-[0px]`
    } else {
      return `${baseClasses} rounded-br-[0px]`
    }
  }

  return baseClasses
})

// Calculate if content should be collapsible based on content height
const isCollapsible = computed(() => {
  if (!data.value?.fields?.body?.content) return false

  // Count total text length in the rich text content
  const content = data.value.fields.body.content
  let totalTextLength = 0

  const countTextLength = (nodes: any[]): number => {
    return nodes.reduce((length, node) => {
      if (node.nodeType === 'paragraph' && node.content) {
        return length + countTextLength(node.content)
      } else if (node.nodeType === 'text' && node.value) {
        return length + node.value.length
      } else if (node.content) {
        return length + countTextLength(node.content)
      }
      return length
    }, 0)
  }

  totalTextLength = countTextLength(content)

  // Make it collapsible if content is longer than 300 characters (roughly 200px height)
  return totalTextLength > 300
})

// Height-based collapse doesn't need preview/expanded documents

// Collapsible state
const isExpanded = ref(false)

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}
</script>
